// Helpers shared by the Form Setup placeholder editor's canvas and panel.

import { camelCaseToFieldName, camelCaseToTitle } from '../../utils/formSetupUtils';
import { imageToView } from '../../utils/formSetupCoords';

// A placeholder is identified by its ancestor path plus its field name — there
// is no id in the stored markup.
export const fieldId = (field: any): string =>
  `${(field?.parents || []).join('.')}::${field?.value}`;

// "Permit > Project > Name". The full ORM path is often five or six levels
// deep, so only the last few segments are worth showing.
export const fieldLabel = (field: any): string => {
  const parts = (field?.parents || [])
    .filter((part: string) => part && part !== 'fields')
    .map(camelCaseToTitle);
  return parts.slice(-3).join(' > ') || field?.value || 'Field';
};

/**
 * The text Preview PDF will actually stamp at this placeholder.
 *
 * `sample_placeholders` (form_setup/helpers.py) prints the node's OWN field
 * name — "Number" — not its path, and `createResult` builds that name with
 * camelCaseToFieldName, so the same call reproduces it exactly.
 *
 * The chip shows this rather than fieldLabel's "Inspected inspections > Permit
 * > Number": at the real print size a three-segment path is several times the
 * width of the word that gets printed, which makes a value look like it will
 * collide with something when it will not. The full path is still the chip's
 * tooltip and is what the properties panel lists.
 */
export const printedLabel = (field: any): string => {
  const parents = field?.parents || [];
  return (
    camelCaseToFieldName(parents[parents.length - 1]) ||
    field?.value ||
    'Field'
  );
};

/**
 * Whether this placeholder can be set to wrap onto multiple lines.
 *
 * Information only. It is the single field `resolve_placeholders`
 * (form_setup/helpers.py) emits as a MULTILINE instruction, and MULTILINE is
 * the only instruction the PDF renderer knows how to lay out inside a box —
 * every other field is drawn as one run from the X/Y corner. Offering the
 * switch anywhere else would be a control that silently does nothing.
 *
 * Matched on printedLabel because that IS the name written into the markup's
 * `field` key, which is the same string the server tests with
 * `raw_field_val.startswith("Information")`. Comparing anything else would let
 * the two drift.
 */
export const isWrappable = (field: any): boolean =>
  printedLabel(field) === 'Information';

/**
 * The placeholder the picker just added, or null.
 *
 * The picker replaces the whole array on every click — one entry longer for an
 * add, one shorter for a remove — so diffing against the previous array is the
 * only way to tell which of the two happened, and to what.
 */
export const addedField = (before: any[], after: any[]): any => {
  const seen = new Set((before || []).map(fieldId));
  return (
    (after || []).find(field => !seen.has(fieldId(field))) || null
  );
};

/** Whether a placeholder with this id is still present. */
export const containsField = (
  fields: any[],
  id: string | null,
): boolean =>
  !!id && (fields || []).some(field => fieldId(field) === id);

// A chip smaller than this is impossible to grab with the mouse. Stored
// box_size is occasionally [0, 0] (branch nodes, or forms saved before box
// sizes were recorded).
export const MIN_CHIP_W = 40;
export const MIN_CHIP_H = 20;

/**
 * Re-derive on-screen geometry from the ORIGINAL stored image pixels.
 *
 * Placeholders are loaded before the form image has loaded, so they start out
 * positioned with the fallback scale. Once the image reports its natural size
 * we know the true ratio and can place them exactly — recomputing from
 * `stored` rather than from the current on-screen values, which would compound
 * the rounding error.
 *
 * Fields the user just added from the picker carry no `stored` and are already
 * in view pixels, so they are left alone.
 */
export const applyScale = (fields: any[], scale: number): any[] =>
  (fields || []).map(field => {
    if (!field?.stored) return field;
    return {
      ...field,
      x: imageToView(field.stored.coordinates[0], scale),
      y: imageToView(field.stored.coordinates[1], scale),
      width: Math.max(MIN_CHIP_W, imageToView(field.stored.boxSize[0], scale)),
      height: Math.max(MIN_CHIP_H, imageToView(field.stored.boxSize[1], scale)),
    };
  });
